import {
  faArrowRight,
  faCheckCircle,
  faEdit,
  faExclamationTriangle,
  faTrash,
  faPercent,
  faTrophy,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../../Axios";
import { UserAuthContext } from "../../context/userContext";

// --- Reusable UI Components (can be moved to their own files) ---

// 1. Profile Skeleton Loader
const ProfileSkeleton = () => (
  <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 md:p-8 animate-pulse">
    <div className="flex flex-col md:flex-row items-center gap-6">
      <div className="w-24 h-24 bg-slate-200 rounded-full flex-shrink-0"></div>
      <div className="flex-grow w-full">
        <div className="h-8 bg-slate-200 rounded w-3/4 mb-2"></div>
        <div className="h-5 bg-slate-200 rounded w-1/2"></div>
      </div>
    </div>
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="flex justify-between items-center">
          <div className="h-4 bg-slate-200 rounded w-1/3"></div>
          <div className="h-4 bg-slate-200 rounded w-1/2"></div>
        </div>
      ))}
    </div>
  </div>
);

// 2. Info Item for key-value pairs
const InfoItem = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-slate-100">
    <dt className="text-sm font-medium text-slate-500">{label}</dt>
    <dd className="mt-1 text-sm text-slate-800 sm:mt-0 font-semibold">
      {value || "N/A"}
    </dd>
  </div>
);

// 3. Initials Avatar
const ProfileAvatar = ({ name = "" }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center border-4 border-white shadow-md">
      <span className="text-3xl font-bold text-indigo-600">{initials}</span>
    </div>
  );
};

// 4. Confirmation Modal
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  type = "info",
}) => {
  if (!isOpen) return null;

  const colors = {
    danger: "bg-red-600 hover:bg-red-700",
    success: "bg-green-600 hover:bg-green-700",
    info: "bg-indigo-600 hover:bg-indigo-700",
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-xl font-bold text-slate-800">{title}</h3>
        <p className="mt-2 text-slate-600">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 text-slate-800 rounded-md hover:bg-slate-300 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-white rounded-md font-semibold ${colors[type]}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Student Profile Component ---

function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authData } = useContext(UserAuthContext);

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    studentId: null,
  });

  // --- Exam Results State ---
  const [examList, setExamList] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState("");
  const [examResults, setExamResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState("");

  // --- Image Upload State ---
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // --- Data Fetching ---
  useEffect(() => {
    const getStudent = async () => {
      try {
        setLoading(true);
        const { data } = await Axios.get(`/student/${id}`);
        setStudent(data);
      } catch (error) {
        console.error("Failed to fetch student data:", error.response);
        toast.error("Could not load student profile.");
        navigate(-1); // Go back if student not found
      } finally {
        setLoading(false);
      }
    };
    getStudent();
  }, [id, navigate]);

  // Fetch exam list
  useEffect(() => {
    const fetchExams = async () => {
      try {
        const { data } = await Axios.get("/exam");
        setExamList(data);
      } catch {
        setExamList([]);
      }
    };
    fetchExams();
  }, []);

  // Fetch selected exam results
  useEffect(() => {
    if (!selectedExamId || !student?.registerNo) {
      setExamResults(null);
      setResultsError("");
      return;
    }
    setResultsLoading(true);
    setExamResults(null);
    setResultsError("");

    Axios.get(`/result/${selectedExamId}/${student.registerNo}`)
      .then(({ data }) => {
        if (data && (data.results || data.cceResults)) {
          setExamResults(data);
        } else if (data && data.message === "Result Not Found") {
          setResultsError("No results found for this exam.");
        } else {
          setResultsError("Result not available.");
        }
      })
      .catch(() => setResultsError("Failed to fetch results."))
      .finally(() => setResultsLoading(false));
  }, [selectedExamId, student?.registerNo]);

  // --- Derived Data using useMemo for performance ---
  const studentAge = useMemo(() => {
    if (!student?.dob) return "N/A";
    const birthDate = new Date(student.dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, [student]);

  // --- Group results by subject (for Exam) ---
  const groupedResults = useMemo(() => {
    if (!examResults?.results) return [];
    const map = {};
    examResults.results.forEach((res) => {
      if (!map[res.subject.subjectName]) map[res.subject.subjectName] = [];
      map[res.subject.subjectName].push(res);
    });
    return Object.entries(map);
  }, [examResults]);

  // --- Group CCE results by subject ---
  const groupedCCEResults = useMemo(() => {
    if (!examResults?.cceResults) return [];
    const map = {};
    examResults.cceResults.forEach((res) => {
      if (!map[res.subject.subjectName]) map[res.subject.subjectName] = [];
      map[res.subject.subjectName].push(res);
    });
    return Object.entries(map);
  }, [examResults]);

  // --- Handlers for Actions ---
  const handleVerify = async () => {
    try {
      setLoading(true);
      await Axios.post(`/student/admission/verify/${modalState.studentId}`);
      toast.success("Student Verified Successfully!");
      setStudent((prev) => ({ ...prev, verified: true })); // Optimistic UI update
    } catch (error) {
      toast.error("Verification failed. Please try again.");
      console.error(error.response);
    } finally {
      setLoading(false);
      setModalState({ isOpen: false });
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await Axios.delete(`/student/${modalState.studentId}`);
      toast.success("Student Deleted Successfully");
      navigate("/all-classes"); // Navigate to a safe page
    } catch (error) {
      toast.error("Deletion failed. Please try again.");
      console.error(error.response);
      setLoading(false);
    } finally {
      setModalState({ isOpen: false });
    }
  };

  const handleDropOut = async (studentId) => {
    try {
      if (
        window.confirm(
          "Are you sure you want to mark this student as dropped out? This action cannot be undone."
        )
      ) {
        setLoading(true);
        await Axios.post(`/student/drop-out/${studentId}`);
        toast.success("Student marked as dropped out successfully.");
        setStudent((prev) => ({ ...prev, droppedOut: true })); // Optimistic UI update
      }
    } catch (error) {
      toast.error("Failed to mark student as dropped out.");
      console.error(error.response);
    } finally {
      setLoading(false);
      setModalState({ isOpen: false });
    }
  };

  const handleConfirmAction = () => {
    if (modalState.type === "verify") handleVerify();
    if (modalState.type === "delete") handleDelete();
  };

  // --- Image Upload Handlers ---
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file)); // show preview

    // Upload image to backend
    const formData = new FormData();
    formData.append("image", file);

    setUploading(true);
    try {
      const { data } = await Axios.patch(`/student/${student._id}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile image updated!");
      setStudent((prev) => ({ ...prev, imageUrl: data.student.imageUrl }));
      setPreviewUrl(null); // clear preview
    } catch (error) {
      console.error("Image upload failed:", error.response);
      toast.error(error.response?.data?.message || "Image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="w-full min-h-screen bg-slate-50 p-4 md:p-8">
        <ProfileSkeleton />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center p-10">
        <h2 className="text-2xl font-bold text-slate-700">Student Not Found</h2>
      </div>
    );
  }

  // --- DROPPED OUT UI + Logic Handling ---
  if (student.droppedOut) {
    return (
      <div className="w-full min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden border border-red-200">
          <div className="flex flex-col items-center gap-6 p-10">
            <FontAwesomeIcon
              icon={faUserSlash}
              className="text-red-500"
              style={{ fontSize: 60 }}
            />
            <h1 className="text-3xl font-bold text-red-800 mb-2">
              Student Dropped Out
            </h1>
            <p className="text-slate-700 text-center">
              This student (<span className="font-semibold">{student.studentName}</span>) has been marked as <span className="font-semibold text-red-600">Dropped Out</span>.<br />
              No further academic actions (exam results, edit, transfer, verification) are available for dropped out students.
            </p>
            <div className="flex flex-col md:flex-row gap-3 mt-8">
              {/* Optionally: allow delete if superAdmin */}
              {authData?.role === "superAdmin" && (
                <button
                  onClick={() =>
                    setModalState({
                      isOpen: true,
                      type: "delete",
                      studentId: student._id,
                    })
                  }
                  className="px-6 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 transition"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
        <ConfirmationModal
          isOpen={modalState.isOpen}
          onClose={() => setModalState({ isOpen: false })}
          onConfirm={handleConfirmAction}
          title="Confirm Deletion"
          message={`This action is irreversible. Are you sure you want to delete ${student.studentName}?`}
          confirmText="Yes, Delete"
          type="danger"
        />
      </div>
    );
  }

  // --- NORMAL (Active) UI ---
  return (
    <>
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false })}
        onConfirm={handleConfirmAction}
        title={
          modalState.type === "verify"
            ? "Confirm Verification"
            : "Confirm Deletion"
        }
        message={
          modalState.type === "verify"
            ? `Are you sure you want to verify ${student.studentName}?`
            : `This action is irreversible. Are you sure you want to delete ${student.studentName}?`
        }
        confirmText={
          modalState.type === "verify" ? "Yes, Verify" : "Yes, Delete"
        }
        type={modalState.type === "verify" ? "success" : "danger"}
      />

      <div className="w-full min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-4 md:p-8">
        {/* --- Profile Card --- */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200/80">
          {/* Card Header */}
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-6 md:p-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row items-center gap-6">
              {/* --- Image Upload and Avatar --- */}
              <div className="relative group">
                {student.registerNo || student.imageUrl || previewUrl ? (
                  <img
                    src={previewUrl || (student.registerNo ? `https://res.cloudinary.com/djal8himk/image/upload/fl_preserve_transparency/v1763118885/students/${student.registerNo}.jpg?_s=public-apps` : student.imageUrl)}
                    alt={student.studentName}
                    className="w-24 h-24 object-cover rounded-full border-4 border-white/80 shadow-md"
                  />
                ) : (
                  <ProfileAvatar name={student.studentName} />
                )}
                {/* Upload overlay button */}
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full shadow-lg ring-2 ring-white/80 opacity-90 hover:opacity-100 transition group-hover:opacity-100"
                  disabled={uploading}
                  title="Change Profile Photo"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  disabled={uploading}
                />
                {uploading && (
                  <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center rounded-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-indigo-600"></div>
                  </div>
                )}
              </div>
              <div className="text-center sm:text-left">
                <h1 className="text-3xl font-extrabold text-white">
                  {student.studentName}
                </h1>
                <p className="text-md text-slate-300 mt-1">
                  Reg. No: {student.registerNo}
                </p>
                {student.verified ? (
                  <span className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-emerald-400/20 text-emerald-300 ring-1 ring-inset ring-emerald-400/30 rounded-full text-sm font-semibold">
                    <FontAwesomeIcon icon={faCheckCircle} /> Verified
                  </span>
                ) : (
                  <span className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-amber-400/20 text-amber-300 ring-1 ring-inset ring-amber-400/30 rounded-full text-sm font-semibold">
                    <FontAwesomeIcon icon={faExclamationTriangle} /> Needs
                    Verification
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-6 md:p-8">
            {/* Personal Info */}
            <section>
              <h3 className="text-lg font-bold text-slate-700 mb-4">
                Personal Information
              </h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <InfoItem label="Father's Name" value={student.fatherName} />
                <InfoItem
                  label="Date of Birth"
                  value={new Date(student.dob).toLocaleDateString("en-GB")}
                />
                <InfoItem
                  label="Phone"
                  value={student.phone ? `+91 ${student.phone}` : "N/A"}
                />
                <InfoItem label="Age" value={`${studentAge} years old`} />
              </dl>
            </section>

            {/* Address Info */}
            <section className="mt-8">
              <h3 className="text-lg font-bold text-slate-700 mb-4">
                Contact & Address
              </h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <InfoItem label="House Name" value={student.houseName} />
                <InfoItem label="Place" value={student.place} />
                <InfoItem label="Post Office" value={student.postOffice} />
                <InfoItem label="Pincode" value={student.pinCode} />
                <InfoItem label="District" value={student.district} />
                <InfoItem label="State" value={student.state} />
              </dl>
            </section>

            {/* Academic Info */}
            <section className="mt-8">
              <h3 className="text-lg font-bold text-slate-700 mb-4">
                Academic Details
              </h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <InfoItem
                  label="Academic Year"
                  value={student.academicYear?.year}
                />
                <InfoItem label="Class" value={student.class?.className} />
                <InfoItem
                  label="Study Center"
                  value={student.branch?.studyCentreName}
                />
              </dl>
            </section>

            {/* Exam Results Selection and Table */}
            <section className="mt-8">
              <h3 className="text-lg font-bold text-slate-700 mb-4">
                Exam Results
              </h3>
              <div className="mb-4">
                <label className="block mb-2 text-sm font-semibold text-slate-600">
                  Select Exam
                </label>
                <select
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-indigo-400"
                  value={selectedExamId}
                  onChange={(e) => setSelectedExamId(e.target.value)}
                >
                  <option value="" disabled>
                    Select an exam
                  </option>
                  {examList.map((exam) => (
                    <option key={exam._id} value={exam._id}>
                      {exam.examName}
                    </option>
                  ))}
                </select>
              </div>
              {/* Results Loading/Error/Empty State */}
              {resultsLoading ? (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-indigo-400"></div>
                  <div className="text-slate-600">Loading results...</div>
                </div>
              ) : resultsError ? (
                <div className="text-center py-6 bg-slate-100 rounded-lg text-slate-500">
                  {resultsError}
                </div>
              ) : examResults &&
                (groupedResults.length > 0 || groupedCCEResults.length > 0) ? (
                <>
                  <h4 className="font-semibold text-slate-700 mb-2">
                    Exam & CCE Marks
                  </h4>
                  <table className="min-w-full text-sm border rounded-xl mb-8">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="py-2 px-3 text-left font-semibold text-slate-600">
                          Subject
                        </th>
                        <th className="py-2 px-3 text-center font-semibold text-slate-600">
                          Exam Marks
                        </th>
                        <th className="py-2 px-3 text-center font-semibold text-slate-600">
                          CCE Marks
                        </th>
                        <th className="py-2 px-3 text-center font-semibold text-slate-600">
                          Total
                        </th>
                        <th className="py-2 px-3 text-center font-semibold text-slate-600">
                          Max Marks
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Get all unique subjects from both exam and CCE */}
                      {Array.from(
                        new Set([
                          ...groupedResults.map(([subject]) => subject),
                          ...groupedCCEResults.map(([subject]) => subject),
                        ])
                      ).map((subject) => {
                        // Exam marks for this subject
                        const examResArr =
                          groupedResults.find(([s]) => s === subject)?.[1] ||
                          [];
                        const examRes = examResArr[0];
                        const examMark =
                          examRes?.marksObtained !== undefined
                            ? examRes.marksObtained
                            : "-";
                        // CCE marks for this subject
                        const cceResArr =
                          groupedCCEResults.find(([s]) => s === subject)?.[1] ||
                          [];
                        const cceRes = cceResArr[0];
                        const cceMark =
                          cceRes?.cceMark !== undefined ? cceRes.cceMark : "-";
                        // Max marks: prefer exam, then cce, then 100
                        const maxMarks =
                          examRes?.maxMarks || cceRes?.maxMarks || 100;
                        // Total
                        let total = "-";
                        if (
                          examMark !== "-" &&
                          cceMark !== "-" &&
                          examMark !== "A" &&
                          cceMark !== "A"
                        ) {
                          total = Number(examMark) + Number(cceMark);
                        } else if (examMark === "A" || cceMark === "A") {
                          total = "A";
                        } else if (examMark !== "-" && cceMark === "-") {
                          total = examMark;
                        } else if (examMark === "-" && cceMark !== "-") {
                          total = cceMark;
                        }
                        return (
                          <tr key={subject} className="even:bg-slate-50/70">
                            <td className="py-2 px-3 font-medium text-slate-700">
                              {subject}
                            </td>
                            <td className="py-2 px-3 text-center font-bold text-slate-800">
                              {examMark}
                            </td>
                            <td className="py-2 px-3 text-center font-bold text-indigo-700">
                              {cceMark}
                            </td>
                            <td className="py-2 px-3 text-center font-bold text-green-700">
                              {total}
                            </td>
                            <td className="py-2 px-3 text-center text-slate-600">
                              {maxMarks}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {examResults.grandTotal !== undefined && (
                      <span className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-semibold">
                        <FontAwesomeIcon icon={faTrophy} />
                        Grand Total: {examResults.grandTotal} /{" "}
                        {examResults.totalPossibleMarks}
                      </span>
                    )}
                    {examResults.percentage !== undefined && (
                      <span className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full font-semibold">
                        <FontAwesomeIcon icon={faPercent} />
                        Percentage: {examResults.percentage}%
                      </span>
                    )}
                  </div>
                </>
              ) : null}
            </section>
          </div>

          {/* Card Footer - Actions */}
          <div className="flex flex-col gap-4 border-t border-slate-200 bg-slate-50 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-3">
              {!student.verified && (
                <button
                  onClick={() =>
                    setModalState({
                      isOpen: true,
                      type: "verify",
                      studentId: student._id,
                    })
                  }
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 font-semibold text-white shadow-md shadow-emerald-500/20 transition hover:bg-emerald-600 disabled:opacity-50"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faCheckCircle} /> Verify Admission
                </button>
              )}
              {authData?.role === "superAdmin" && (
                <Link
                  to={`/transfer-student/${student._id}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
                >
                  <FontAwesomeIcon icon={faArrowRight} /> Transfer
                </Link>
              )}
              {authData?.role === "superAdmin" && (
                <Link
                  to={`/edit-student/${student._id}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
                >
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </Link>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleDropOut(student._id)}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 font-semibold text-rose-600 shadow-sm ring-1 ring-inset ring-rose-200 transition hover:bg-rose-50 disabled:opacity-50"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faUserSlash} /> Drop Out
              </button>
              <button
                onClick={() =>
                  setModalState({
                    isOpen: true,
                    type: "delete",
                    studentId: student._id,
                  })
                }
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-5 py-2.5 font-semibold text-white shadow-md shadow-rose-500/20 transition hover:bg-rose-700 disabled:opacity-50"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default StudentProfile;